import { jsx as _jsx } from "react/jsx-runtime";
import { ClipUtils } from "@air/utils-clips";
import { reportErrorToBugsnag } from "@air/utils-error";
import { getVideoFormatFromUrl } from "@air/utils-video";
import classNames from "classnames";
import noop from "lodash/noop";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMountedState } from "__barrel_optimize__?names=useMountedState!=!react-use";
import { isHlsSupported, loadHls } from "~/utils/HlsUtils";
export const ClipPreview = (param)=>{
    let { status, vertical, src, playbackRate } = param;
    const isMounted = useMountedState();
    const hlsRef = useRef();
    const ref = useRef(null);
    const playPromise = useRef(Promise.resolve());
    const isPlaying = useRef(false);
    const [loaded, setLoaded] = useState(false);
    const transcoded = ClipUtils.isTranscoded(status);
    // Implementation needed to avoid an exception.
    // https://stackoverflow.com/questions/36803176/how-to-prevent-the-play-request-was-interrupted-by-a-call-to-pause-error/40370077#40370077
    const playVideo = useCallback(()=>{
        if (ref.current && ref.current.paused && !isPlaying.current) {
            var _ref_current_play;
            // catch and do nothing to ignore this error
            // https://developers.google.com/web/updates/2017/06/play-request-was-interrupted
            playPromise.current = (_ref_current_play = ref.current.play()) === null || _ref_current_play === void 0 ? void 0 : _ref_current_play.catch(noop);
        }
    }, []);
    const pauseVideo = useCallback(()=>{
        if (ref.current && !ref.current.paused && isPlaying.current) {
            ref.current.pause();
        }
    }, []);
    useEffect(()=>{
        if (loaded && ref.current) {
            playVideo();
        }
    }, [
        loaded,
        pauseVideo,
        playVideo
    ]);
    useEffect(()=>{
        const loadVideo = async ()=>{
            if (ref.current) {
                ref.current.playbackRate = playbackRate;
                ref.current.onplaying = ()=>{
                    isPlaying.current = true;
                };
                ref.current.onpause = ()=>{
                    isPlaying.current = false;
                };
            }
            const fmt = getVideoFormatFromUrl(src);
            if (fmt !== "m3u8") {
                if (ref.current) {
                    ref.current.src = src || "";
                    //@ts-ignore video doesn't have a type prop?
                    ref.current.type = "video/".concat(fmt);
                }
                setLoaded(true);
                return;
            }
            if (!isHlsSupported()) {
                setLoaded(true);
                return;
            }
            try {
                var _hlsRef_current;
                (_hlsRef_current = hlsRef.current) === null || _hlsRef_current === void 0 ? void 0 : _hlsRef_current.destroy();
                hlsRef.current = await loadHls({
                    src,
                    videoElement: ref.current
                });
                if (isMounted()) {
                    setLoaded(true);
                } else {
                    return;
                }
            } catch (error) {
                reportErrorToBugsnag({
                    error,
                    context: "Failed to load new Hls source in ClipPreview",
                    metadata: {
                        key: "Data",
                        data: {
                            src
                        }
                    }
                });
            }
        };
        loadVideo();
    }, [
        playbackRate,
        src,
        isMounted
    ]);
    useEffect(()=>{
        return ()=>{
            var _hlsRef_current;
            (_hlsRef_current = hlsRef.current) === null || _hlsRef_current === void 0 ? void 0 : _hlsRef_current.destroy();
        };
    }, []);
    return /*#__PURE__*/ _jsx("video", {
        className: classNames("pointer-events-none absolute left-0 top-0 h-full w-full rounded-4 bg-black", vertical ? "object-contain" : "object-cover", transcoded ? "block" : "hidden"),
        muted: true,
        loop: true,
        playsInline: true,
        ref: ref,
        src: src
    });
};
